import React, { useState, useEffect } from 'react';
import {
  Box,
  Stack,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  Paper,
  Button,
  CircularProgress,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import { translate as t } from 'hooks/useTranslations';

interface StepDistributionScheduleProps {
  onCancel?: () => void;
  onBack?: () => void;
  onAssign?: () => void;
  value?: {
    dueDate: string;
  };
  onChange?: (v: { dueDate: string }) => void;
  loading?: boolean;
}

const StepDistributionSchedule: React.FC<StepDistributionScheduleProps> = ({
  onCancel,
  onBack,
  onAssign,
  value,
  onChange,
  loading,
}) => {
  const [dueDate, setDueDate] = useState<Dayjs | null>(value?.dueDate ? dayjs(value.dueDate) : null);
  const [pickerValue, setPickerValue] = useState<Dayjs | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);

  useEffect(() => {
    if (onChange) {
      onChange({ dueDate: dueDate ? dueDate.toISOString() : '' });
    }
  }, [dueDate]);

  useEffect(() => {
    if (!isTyping) {
      if (value?.dueDate && dayjs(value.dueDate).isValid()) {
        setPickerValue(dayjs(value.dueDate));
        setInputValue(dayjs(value.dueDate).format('MM/DD/YYYY'));
      } else {
        setPickerValue(null);
        setInputValue('');
      }
    }
  }, [value?.dueDate, isTyping]);

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', minHeight: '80vh', width: '100%' }}>
      <Paper
        sx={{
          p: 4,
          maxWidth: 900,
          width: '100%',
          mt: 8,
          minHeight: 500,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        <Stack spacing={4}>
          <Box>
            <Typography
              variant='subtitle1'
              sx={{ fontWeight: 600, mb: 2 }}
            >
              {t('dtbs.assignments.stepper.distributionSchedule')}
            </Typography>
            <RadioGroup value='together'>
              <FormControlLabel
                value='together'
                control={<Radio color='success' />}
                label={
                  <>
                    <Typography sx={{ fontWeight: 600 }}>{t('dtbs.assignments.stepper.sendTogether')}</Typography>
                    <Typography
                      variant='body2'
                      color='text.secondary'
                    >
                      {t('dtbs.assignments.stepper.sendAllTogether')}
                    </Typography>
                  </>
                }
              />
            </RadioGroup>
          </Box>
          <Box>
            <Typography
              variant='subtitle1'
              sx={{ fontWeight: 600, mb: 2 }}
            >
              {t('dtbs.assignments.stepper.dueDateSettings')}
            </Typography>
            <Typography
              variant='body2'
              color='text.secondary'
              sx={{ mb: 1 }}
            >
              {t('dtbs.assignments.stepper.allDtbsDeliveredTogether')}
            </Typography>
            <DatePicker
              label={t('dtbs.assignments.stepper.dueDate')}
              value={pickerValue}
              onChange={(newDate) => {
                setPickerValue(newDate);
                setDueDate(newDate);
              }}
              slotProps={{ textField: { fullWidth: false } }}
            />
          </Box>
        </Stack>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 4 }}>
          <Typography variant='body2'>
            {t('dtbs.assignments.stepper.step').replace('{{current}}', '3').replace('{{total}}', '3')}
          </Typography>
          <Stack
            direction='row'
            spacing={2}
          >
            <Button
              variant='outlined'
              onClick={onBack}
            >
              {t('dtbs.assignments.stepper.back')}
            </Button>
            <Button
              variant='contained'
              onClick={onAssign}
              disabled={loading}
              endIcon={loading ? <CircularProgress size={16} /> : undefined}
            >
              {t('dtbs.assignments.stepper.assignPackage')}
            </Button>
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
};

export default StepDistributionSchedule;
